import platform from "platform"
import React, { ReactElement } from "react"
import { useMediaQuery } from "react-responsive"
import {
  FeeUnit,
  Parking,
  ParkingAvailability_Summary,
  Parking_Type,
  SubMaxFee,
} from "../../__generated__/proto/itech/motorist/pksha/v1/parking"
import * as config from "../../config"
import {
  FeeTableExtension,
  FeeUnitExtension,
  HourlyMaxFeeExtension,
  SubMaxFeeExtension,
} from "../../domain/parking-extensions"
import * as styles from "./parking-detail-info-table.module.scss"

interface Props {
  parking: Parking
  zipcode: string | null
}

const defaultInfoRow = (key: string, value: string): ReactElement => (
  <div className={styles.infoRow}>
    <div className={styles.infoKeySection}>{key}</div>
    <div className={styles.infoValueSection}>{value}</div>
  </div>
)

const addressInfoRow = (
  parking: Parking,
  zipcode: string | null
): ReactElement => {
  const zipcodeText = zipcode !== null ? `〒${zipcode}` : ""
  return (
    <div className={styles.infoRow}>
      <div className={styles.infoKeySection}>住所</div>
      <div className={styles.infoValueSection}>
        <p className={styles.addressText}>
          {zipcodeText} {parking.fullAddress}
        </p>
        <a
          className={styles.openGoogleMapButton}
          href={`https://maps.google.com/maps?q=${parking.fullAddress}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Google mapで開く
        </a>
        <a
          className={styles.copyAddressButton}
          onClick={e => {
            e.preventDefault()
            navigator.clipboard.writeText(parking.fullAddress)
          }}
        >
          住所をコピー
        </a>
      </div>
    </div>
  )
}

const feesInfoRow = (
  feesMap: { [key: string]: string[] } | null
): ReactElement => {
  return (
    <div className={styles.infoRow}>
      <div className={styles.infoKeySection}>料金</div>
      <div className={styles.infoValueSection}>
        <div>
          {feesMap &&
            Object.entries(feesMap).map(entry => {
              const section = entry[0]
              const fees = entry[1]
              return (
                <div key={section} className={styles.feesRow}>
                  <p>{section}</p>
                  <div className={styles.feesText}>
                    {fees.length > 0
                      ? fees.map((fee, index) => <p key={index}>{fee}</p>)
                      : "不明"}
                  </div>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}

const availableServicesInfoRow = (parking: Parking): ReactElement => {
  const appURL =
    platform.os?.family === "iOS"
      ? config.QT_NET_APP_IOS_URL
      : platform.os?.family === "Android"
      ? config.QT_NET_APP_ANDROID_URL
      : null

  return (
    <div className={styles.infoRow}>
      <div className={styles.infoKeySection}>利用可能なサービス</div>
      <div className={styles.infoValueSection}>
        <div className={styles.servicesText}>
          {parking.isQtNetAvailable && <p>QT-net ポイント貯まる</p>}
          {parking.isQtNetAvailable && parking.isSmartPayAvailable && (
            <p>QT-net SmartPay利用可能</p>
          )}
          {parking.isQtNetAvailable && parking.reservable && (
            <p>QT-net スマホ予約可能</p>
          )}
        </div>
        {appURL && (
          <a
            className={styles.downloadQtNetButton}
            href={appURL}
            target="_blank"
            rel="noopener noreferrer"
          >
            QT-netアプリをダウンロード
          </a>
        )}
      </div>
    </div>
  )
}

export const ParkingDetailInfoTable = React.memo(
  function ParkingDetailInfoTable(props: Props): ReactElement {
    // MARK: - Variables

    const { parking, zipcode } = props
    const isMobile = useMediaQuery({ maxWidth: config.RESPONSIVE_BREAKPOINT })

    // MARK: - Render

    return (
      <div className={styles.container}>
        {addressInfoRow(parking, zipcode)}
        {defaultInfoRow("車室数", totalSpaceCount())}
        {feesInfoRow(feesMap(parking))}
        {defaultInfoRow("設備", handicappedSpaces())}
        {availableServicesInfoRow(parking)}
        {defaultInfoRow("駐車場タイプ", typeText())}
        {defaultInfoRow("備考", parking.note)}
      </div>
    )

    // MARK: - Methods

    function handicappedSpaces(): string {
      const handicappedSpaces = parking.handicappedSpaceNumbers.map(
        num => `${num}番車室`
      )

      return `車椅子対応：${
        handicappedSpaces.length > 0 ? handicappedSpaces.join("、") : "未対応"
      }`
    }

    function totalSpaceCount(): string {
      const availability = parking.availability
      if (
        availability == null ||
        availability.summary == ParkingAvailability_Summary.SUMMARY_UNOBTAINABLE
      ) {
        return "不明"
      }
      return `${availability.totalSpaceCount}車室`
    }

    function feesMap(parking: Parking): { [key: string]: string[] } | null {
      const feeTable = parking.feeTable
      if (!feeTable) {
        return null
      }

      const weekdayFees = feesOfTheDay(
        feeTable.weekdayFees,
        FeeTableExtension.weekdaySubMaxFee(feeTable)
      )
      const saturdayFees = feesOfTheDay(
        feeTable.saturdayFees,
        FeeTableExtension.saturdaySubMaxFee(feeTable)
      )
      const sundayFees = feesOfTheDay(
        feeTable.sundayFees,
        FeeTableExtension.sundaySubMaxFee(feeTable)
      )
      const holidayFees = feesOfTheDay(
        feeTable.holidayFees,
        FeeTableExtension.holidaySubMaxFee(feeTable)
      )

      return {
        平日: weekdayFees,
        土曜: saturdayFees,
        日曜: sundayFees,
        祝日: holidayFees,
      }
    }

    function feesOfTheDay(
      feeUnits: FeeUnit[],
      subMaxFee: SubMaxFee | undefined
    ): string[] {
      const fees: string[] = []
      feeUnits.forEach(fu => {
        const startTime = fu.startTime
        const endTime = fu.endTime
        if (!startTime || !endTime) {
          return
        }

        const startHour = startTime.hours.toString().padStart(2, "0")
        const startMinute = startTime.minutes.toString().padStart(2, "0")
        const endHour = endTime.hours.toString().padStart(2, "0")
        const endMinute = endTime.minutes.toString().padStart(2, "0")
        const unitMinutes = FeeUnitExtension.wrappedUnitDurationInMinutes(fu)
        const yen = FeeUnitExtension.wrappedFeePerUnitDuration(fu)
        if (!unitMinutes || !yen) {
          return
        }

        const timeFeeText = `${startHour}:${startMinute}-${endHour}:${endMinute} ${unitMinutes}分/${yen}円`
        let maxFeeText = ""

        if (FeeUnitExtension.isValidFeeUnitMaxFee(fu)) {
          const maxFee = FeeUnitExtension.wrappedMaxFee(fu)
          if (!maxFee) {
            return
          }
          maxFeeText = `時間帯最大料金 ${maxFee}円`
        } else if (
          subMaxFee?.hourly?.hourDuration &&
          subMaxFee?.hourly?.feePerHour
        ) {
          const hours = HourlyMaxFeeExtension.wrappedHourDurationInHours(
            subMaxFee.hourly
          )
          const maxFee = HourlyMaxFeeExtension.wrappedFeePerHour(
            subMaxFee.hourly
          )
          if (!hours || !maxFee) {
            return
          }
          maxFeeText = `${hours}時間以内最大料金 ${maxFee}円`
        } else if (subMaxFee?.daily) {
          const maxFee = SubMaxFeeExtension.wrappedDaily(subMaxFee)
          if (!maxFee) {
            return
          }
          maxFeeText = `当日最大料金 ${maxFee}円`
        }

        if (isMobile) {
          // 画面サイズが小さいので、2行に分ける
          fees.push(timeFeeText)
          fees.push(maxFeeText)
        } else {
          fees.push(`${timeFeeText}　${maxFeeText}`)
        }
      })
      return fees
    }

    function typeText(): string {
      switch (parking.type) {
        case Parking_Type.TYPE_INVALID:
          return "不明"
        case Parking_Type.TYPE_LOCK:
          return "ロック板"
        case Parking_Type.TYPE_LOCKLESS:
          return "ロックレス"
        case Parking_Type.TYPE_GATE:
          return "ゲート式"
        case Parking_Type.TYPE_PHSLOCK:
          return "PHS式ロック板"
      }
    }
  }
)
